// ============================================================================
// KLINISOL CRO - Login Component (WITH 2FA)
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// Pure Tailwind CSS - FDA 21 CFR Part 11 Compliant
// Two-Factor Authentication via Email OTP
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogIn, Loader2, ChevronDown, ChevronUp, Shield, Mail, RefreshCw } from 'lucide-react';
import Navbar from './Navbar';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [acceptanceChecked, setAcceptanceChecked] = useState(false);
    const [disclaimerOpen, setDisclaimerOpen] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    // 2FA State
    const [show2FA, setShow2FA] = useState(false);
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const [resendCooldown, setResendCooldown] = useState(0);
    const otpRefs = useRef([]);
    
    const { login, verifyOTP, resendOTP } = useAuth();
    const navigate = useNavigate();

    // Cooldown timer for resend
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // Focus first OTP input when 2FA shows
    useEffect(() => {
        if (show2FA && otpRefs.current[0]) {
            otpRefs.current[0].focus();
        }
    }, [show2FA]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password);

        if (result.success) {
            if (result.requires2FA) {
                setShow2FA(true);
                setResendCooldown(60);
            } else {
                navigate('/dashboard');
            }
        } else {
            setError(result.message || 'Invalid credentials');
        }

        setLoading(false);
    };

    const handleOtpChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) return;

        const newDigits = [...otpDigits];
        newDigits[index] = value;
        setOtpDigits(newDigits);

        // Auto-focus next input
        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all digits filled
        if (value && index === 5 && newDigits.every(d => d)) {
            handleVerifyOTP(newDigits.join(''));
        }
    };

    const handleOtpKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pastedData.length === 6) {
            const newDigits = pastedData.split('');
            setOtpDigits(newDigits);
            handleVerifyOTP(pastedData);
        }
    };

    const handleVerifyOTP = async (otp) => {
        setError('');
        setLoading(true);

        const result = await verifyOTP(email, otp);

        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.message || 'Invalid verification code');
            setOtpDigits(['', '', '', '', '', '']);
            otpRefs.current[0]?.focus();
        }

        setLoading(false);
    };

    const handleResendOTP = async () => {
        if (resendCooldown > 0) return;
        
        setError('');
        setLoading(true);

        const result = await resendOTP(email);

        if (result.success) {
            setResendCooldown(60);
            setOtpDigits(['', '', '', '', '', '']);
            otpRefs.current[0]?.focus();
        } else {
            setError(result.message || 'Failed to resend code');
        }

        setLoading(false);
    };

    const handleBackToLogin = () => {
        setShow2FA(false);
        setOtpDigits(['', '', '', '', '', '']);
        setError('');
        setPassword('');
    };

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-br from-[#475569] via-[#E91E63] to-[#475569] flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    {/* Logo Section */}
                    <div className="text-center mb-8">
                        <h1 className="text-5xl font-bold text-white mb-2">
                            KLIN<span className="text-[#E91E63]">i</span>SOL
                        </h1>
                        <p className="text-white text-sm opacity-90 mb-1">A Boutique CRO</p>
                        <p className="text-white text-xs opacity-75">Advancing Investigator-Initiated Research</p>
                    </div>

                    {/* Login Card */}
                    <div className="bg-white rounded-2xl shadow-2xl p-8">
                        {!show2FA ? (
                            <>
                                {/* Standard Login Form */}
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    <LogIn className="w-6 h-6 text-[#475569]" />
                                    <h2 className="text-2xl font-bold text-gray-900">Login</h2>
                                </div>

                                {error && (
                                    <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                                        <p className="text-sm font-medium">{error}</p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {/* Email Field */}
                                    <div>
                                        <label 
                                            htmlFor="email" 
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                        >
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            placeholder="Enter your email"
                                            disabled={loading}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        />
                                    </div>

                                    {/* Password Field */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label 
                                                htmlFor="password" 
                                                className="block text-sm font-semibold text-gray-700"
                                            >
                                                Password
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => navigate('/forgot-password')}
                                                className="text-xs text-[#E91E63] hover:text-[#475569] font-medium transition-colors"
                                            >
                                                Forgot Password?
                                            </button>
                                        </div>
                                        <input
                                            type="password"
                                            id="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            placeholder="Enter your password"
                                            disabled={loading}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        />
                                    </div>

                                    {/* Compliance Disclaimer - Collapsible */}
                                    <div className="bg-gradient-to-r from-[#475569]/10 to-[#E91E63]/10 border-l-4 border-[#475569] rounded-r-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setDisclaimerOpen(!disclaimerOpen)}
                                            className="w-full p-4 flex items-center justify-between text-left hover:from-[#475569]/15 hover:to-[#E91E63]/15 transition-colors"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-[#475569] font-bold text-xs">
                                                    ⚠️ AUTHORIZED ACCESS ONLY
                                                </span>
                                            </div>
                                            {disclaimerOpen ? (
                                                <ChevronUp className="w-5 h-5 text-[#475569] flex-shrink-0" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-[#475569] flex-shrink-0" />
                                            )}
                                        </button>
                                        
                                        {disclaimerOpen && (
                                            <div className="px-4 pb-4 text-gray-700 text-xs leading-relaxed border-t border-[#475569]/20">
                                                <p className="mb-2 mt-3">
                                                    This system contains confidential clinical research data protected under 21 CFR Part 11, ICH-GCP, and HIPAA regulations. Access is restricted to authorized personnel only.
                                                </p>
                                                <ul className="list-disc list-inside space-y-1 mb-2">
                                                    <li>All activities are monitored and logged for regulatory compliance</li>
                                                    <li>Unauthorized access is prohibited and subject to prosecution</li>
                                                    <li>By logging in, you acknowledge compliance with all applicable SOPs</li>
                                                </ul>
                                                <p className="font-semibold text-[#475569]">
                                                    If you are not authorized, disconnect immediately.
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Acceptance Checkbox */}
                                    <div className="flex items-start gap-2 pt-2">
                                        <input
                                            type="checkbox"
                                            id="acceptance"
                                            checked={acceptanceChecked}
                                            onChange={(e) => setAcceptanceChecked(e.target.checked)}
                                            disabled={loading}
                                            className="mt-1 w-4 h-4 text-[#475569] border-gray-300 rounded focus:ring-[#475569] focus:ring-2"
                                        />
                                        <label htmlFor="acceptance" className="text-xs text-gray-700 leading-relaxed">
                                            I acknowledge that I am an authorized user and agree to comply with all 
                                            applicable regulations, SOPs, and the{' '}
                                            <button
                                                type="button"
                                                onClick={() => navigate('/terms-and-conditions')}
                                                className="text-[#475569] underline hover:text-[#E91E63] font-semibold transition-colors"
                                            >
                                                Terms & Conditions
                                            </button>
                                        </label>
                                    </div>

                                    {/* Submit Button */}
                                    <button 
                                        type="submit" 
                                        disabled={loading || !acceptanceChecked}
                                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#1e293b] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                <span>Logging in...</span>
                                            </>
                                        ) : (
                                            <>
                                                <LogIn className="w-5 h-5" />
                                                <span>Login</span>
                                            </>
                                        )}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <>
                                {/* 2FA Verification Form */}
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    <Shield className="w-6 h-6 text-[#475569]" />
                                    <h2 className="text-2xl font-bold text-gray-900">Verification</h2>
                                </div>

                                <div className="flex items-center justify-center gap-2 mb-6 text-gray-600">
                                    <Mail className="w-5 h-5" />
                                    <p className="text-sm">Code sent to <span className="font-semibold">{email}</span></p>
                                </div>

                                {error && (
                                    <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                                        <p className="text-sm font-medium">{error}</p>
                                    </div>
                                )}

                                {/* OTP Input */}
                                <div className="flex justify-center gap-2 mb-6">
                                    {otpDigits.map((digit, index) => (
                                        <input
                                            key={index}
                                            ref={el => otpRefs.current[index] = el}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            onPaste={handleOtpPaste}
                                            disabled={loading}
                                            className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all disabled:bg-gray-100"
                                        />
                                    ))}
                                </div>

                                <p className="text-center text-xs text-gray-500 mb-6">
                                    Enter the 6-digit code sent to your email. Code expires in 5 minutes.
                                </p>

                                {/* Verify Button */}
                                <button 
                                    onClick={() => handleVerifyOTP(otpDigits.join(''))}
                                    disabled={loading || otpDigits.some(d => !d)}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#1e293b] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl mb-4"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Verifying...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Shield className="w-5 h-5" />
                                            <span>Verify Code</span>
                                        </>
                                    )}
                                </button>

                                {/* Resend & Back buttons */}
                                <div className="flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={handleBackToLogin}
                                        disabled={loading}
                                        className="text-sm text-gray-600 hover:text-[#475569] transition-colors"
                                    >
                                        ← Back to login
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleResendOTP}
                                        disabled={loading || resendCooldown > 0}
                                        className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#E91E63] disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="text-center mt-6">
                        <p className="text-white text-xs opacity-75">
                            &copy; 2025 KLINiSOL. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
